import { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import 'react-toastify/dist/ReactToastify.css';
import Dashboard from './pages/Dashboard';
import RetailSales from './pages/RetailSales';
import Repairs from './pages/Repairs';
import Customers from './pages/Customers';
import Products from './pages/Products';
import StockReport from './pages/StockReport';
import InventoryCount from './pages/InventoryCount';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Login from './pages/Login';
import ScrapPurchase from './pages/ScrapPurchase';
import Campaigns from './pages/Campaigns';
import TerminalPanel from './pages/TerminalPanel';
import PersonnelManagement from './pages/PersonnelManagement';
import PaymentCalendar from './pages/PaymentCalendar';
import WholesalerPayments from './pages/WholesalerPayments';
import Wholesalers from './pages/Wholesalers';
import WholesalerDetail from './pages/WholesalerDetail';
import ShopifySettings from './pages/ShopifySettings';
import PazaryeriGateway from './pages/PazaryeriGateway';
import TrendyolSettings from './pages/TrendyolSettings';
import Workshop from './pages/Workshop';
import TamiratTakip from './pages/TamiratTakip';
import ReceiptPreview from './pages/ReceiptPreview';
import Downloads from './pages/Downloads';
import Legal from './pages/Legal';
import LabelDesign from './pages/LabelDesign';
import logService from './services/logService';
import activityService, { PAGE_NAMES } from './services/activityService';
import { API_URL } from './config/api';
import {
  startSystemPrintHostHeartbeat,
  stopSystemPrintHostHeartbeat,
  startSystemPrintHostJobPolling,
  stopSystemPrintHostJobPolling,
} from './utils/systemPrintRelay';
import './App.css';
import './components/Loading.css';

// Check if running as native app (Capacitor)
const isNativeApp = Capacitor.isNativePlatform();

// Protected Route Component
const ProtectedRoute = ({ children, user }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Scroll to Top Component - Sayfa değiştiğinde en üste scroll yap ve aktivite takibi
const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Sayfa görüntüleme aktivitesini kaydet
    const pageName = PAGE_NAMES[location.pathname] || location.pathname;
    activityService.pageView(pageName, location.pathname);
  }, [location.pathname]);

  return null;
};

// Store Selector Component - Sadece Dashboard header'da gösteriliyor
const StoreSelector = () => {
  // Mağaza seçici artık sadece Dashboard header'da
  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(() => {
    const saved = localStorage.getItem('adminSelectedStore');
    return saved ? JSON.parse(saved) : null;
  });

  // Mağazaları yükle
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await fetch(`${API_URL}/stores`);
        const data = await response.json();
        setStores(data);

        // Admin kullanıcı ve mağaza seçili değilse ilk mağazayı seç
        if (data.length > 0 && !selectedStore) {
          const firstStore = data[0];
          setSelectedStore(firstStore);
          localStorage.setItem('adminSelectedStore', JSON.stringify(firstStore));
        }
      } catch (error) {
        console.error('Mağaza yükleme hatası:', error);
      }
    };
    fetchStores();
  }, []);

  // Kasiyer kullanıcılar için mağazayı otomatik ayarla
  useEffect(() => {
    if (user && user.role !== 'admin' && user.store_id && stores.length > 0) {
      const userStore = stores.find(s => s.id === user.store_id);
      if (userStore) {
        setSelectedStore(userStore);
      }
    }
  }, [user, stores]);

  // Sistem yazıcı host kayıt: kullanıcı giriş yaptığında ve store_id biliniyorken,
  // bu cihazda direkt yazdırma açık + bir yazıcı seçili ise mağaza yazdırma host'u
  // olarak heartbeat gönderir. Tüm uygulamada (her sayfada) aktif.
  useEffect(() => {
    const effectiveStoreId = user
      ? (user.role === 'admin' ? selectedStore?.id : user.store_id)
      : null;
    if (effectiveStoreId) {
      startSystemPrintHostHeartbeat(effectiveStoreId);
      startSystemPrintHostJobPolling();
    } else {
      stopSystemPrintHostHeartbeat();
      stopSystemPrintHostJobPolling();
    }
    return () => {
      stopSystemPrintHostHeartbeat();
      stopSystemPrintHostJobPolling();
    };
  }, [user, selectedStore]);

  // Log context (kullanıcı + mağaza) her sayfada güncel tutulsun.
  // Önceden yalnızca RetailSales bunu ayarlıyordu; diğer sayfalarda (Repairs, ScrapPurchase, ...)
  // loglar store_id/store_name olmadan (NULL) kaydediliyordu.
  useEffect(() => {
    logService.setContext(user, selectedStore);
  }, [user, selectedStore]);

  // Mağaza seçimi değiştiğinde localStorage'a kaydet
  const handleStoreChange = (storeId) => {
    const store = stores.find(s => s.id === parseInt(storeId));
    if (store) {
      setSelectedStore(store);
      localStorage.setItem('adminSelectedStore', JSON.stringify(store));
    }
  };

  // Sayfa yüklendiğinde token kontrolü
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          // Token'ı doğrula
          const response = await fetch(`${API_URL}/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
            // LocalStorage'ı da güncelle (izinler değişmiş olabilir)
            localStorage.setItem('user', JSON.stringify(data.user));
            // Log context ayarla
            logService.setContext(data.user, null);
            // Aktivite servisi context ayarla
            activityService.setContext(data.user, null);
          } else {
            // Token geçersiz, temizle
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Auth check error:', error);
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  // Login handler
  const handleLogin = (userData) => {
    setUser(userData);
    // Log context ayarla ve login log'u kaydet
    logService.setContext(userData, null);
    logService.loginSuccess(userData);
    // Aktivite servisi context ayarla
    activityService.setContext(userData, null);
    activityService.custom('Giriş Yapıldı', { user_id: userData.id, user_name: userData.name });
  };

  // Logout handler
  const handleLogout = useCallback(() => {
    // Çıkış log'u kaydet
    logService.logout();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  // Backend oturumu kabul etmediğinde (token süresi doldu / iptal edildi) giriş ekranına dön — bkz. utils/apiAuth.js
  useEffect(() => {
    const onUnauthorized = () => {
      if (!user) return;
      toast.warning('Oturumunuzun süresi doldu, lütfen tekrar giriş yapın.', { toastId: 'session-expired' });
      handleLogout();
    };
    window.addEventListener('akik:unauthorized', onUnauthorized);
    return () => window.removeEventListener('akik:unauthorized', onUnauthorized);
  }, [user, handleLogout]);

  // Tamirat hatırlatma sistemi
  const reminderIntervalRef = useRef(null);

  const checkRepairReminders = useCallback(async () => {
    const enabled = localStorage.getItem('repairReminderEnabled');
    if (enabled === 'false' || !user) return;

    const hours = parseInt(localStorage.getItem('repairReminderHours') || '24');

    try {
      const response = await fetch(`${API_URL}/repairs`);
      const data = await response.json();
      if (!Array.isArray(data)) return;

      const now = new Date();
      // Mağaza bazlı filtreleme: admin tüm mağazaları görür, diğer kullanıcılar sadece kendi mağazasındaki
      // tamiratları (ve mağazaya atanmamış legacy kayıtları) bildirim olarak alır.
      const userStoreId = user?.store_id;
      const isAdmin = user?.role === 'admin';
      const urgentRepairs = data.filter(r => {
        if (!r.deadline) return false;
        if (r.status === 'delivered') return false;
        if (r.reminder_dismissed) return false;
        if (!isAdmin && userStoreId && r.store_id && r.store_id !== userStoreId) return false;
        const deadline = new Date(r.deadline);
        const hoursLeft = (deadline - now) / (1000 * 60 * 60);
        return hoursLeft <= hours && hoursLeft > -48;
      });

      urgentRepairs.forEach(repair => {
        const deadlineStr = new Date(repair.deadline).toLocaleDateString('tr-TR');

        const statusText = repair.status === 'received' ? 'Teslim Alındı' :
          repair.status === 'sent_to_workshop' ? 'Tamirata Yolda' :
          repair.status === 'at_workshop' ? 'Atölyede' :
          repair.status === 'in_progress' ? 'Tamiratta' :
          repair.status === 'completed' ? 'Tamirat Tamamlandı' :
          repair.status === 'returning_to_store' ? 'Mağazaya Yolda' :
          repair.status === 'ready_for_pickup' ? 'Tamirattan Geldi' :
          repair.status === 'checked' ? 'Kontrol Edildi' : '';

        if (!statusText) return;

        toast.warning(
          `[Tamirat] #${repair.id} ${repair.customer_name} — Son teslim: ${deadlineStr}, Durum: ${statusText}. Lütfen ilgilenin!`,
          { autoClose: 15000, toastId: `repair-reminder-${repair.id}` }
        );
      });
    } catch (error) {
      // sessiz hata
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const enabled = localStorage.getItem('repairReminderEnabled');
    if (enabled === 'false') return;

    const intervalMin = parseInt(localStorage.getItem('repairReminderIntervalMin') || '15');

    // İlk kontrol 30 saniye sonra
    const initialTimeout = setTimeout(() => {
      checkRepairReminders();
    }, 30000);

    // Periyodik kontrol
    reminderIntervalRef.current = setInterval(checkRepairReminders, intervalMin * 60 * 1000);

    return () => {
      clearTimeout(initialTimeout);
      if (reminderIntervalRef.current) clearInterval(reminderIntervalRef.current);
    };
  }, [user, checkRepairReminders]);

  // Yüklenme ekranı
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <img src="/logo.png" alt="Akik Gümüş" className="loading-logo" />
          <div className="loading-bars">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        {/* Admin Mağaza Seçici - Sadece ana sayfada */}
        <StoreSelector
          user={user}
          selectedStore={selectedStore}
          stores={stores}
          onStoreChange={handleStoreChange}
        />
        <ToastContainer
          position="top-right"
          autoClose={4500}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
          icon={({ type }) => {
            switch (type) {
              case 'success':
                return <CheckCircle size={20} color="#ffffff" />;
              case 'error':
                return <XCircle size={20} color="#ffffff" />;
              case 'warning':
                return <AlertTriangle size={20} color="#ffffff" />;
              case 'info':
                return <Info size={20} color="#ffffff" />;
              default:
                return null;
            }
          }}
        />
        <Routes>
          {/* Login Route - Giriş yapmamış kullanıcılar için */}
          <Route
            path="/login"
            element={
              user ? (
                user.role === 'terminal' ? <Navigate to="/terminal" replace /> :
                user.role === 'atolye' ? <Navigate to="/workshop" replace /> :
                <Navigate to="/" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />

          {/* Tamirat Takip - Public sayfa (giriş gerektirmez) */}
          <Route
            path="/tamirat"
            element={<TamiratTakip />}
          />

          {/* Gizli Fiş Önizleme - token korumalı, giriş gerektirmez */}
          <Route
            path="/repairs/:token"
            element={<ReceiptPreview />}
          />

          {/* APK İndirme - pin korumalı, giriş gerektirmez */}
          <Route
            path="/downloads"
            element={<Downloads />}
          />

          {/* Terminal Panel Route */}
          <Route
            path="/terminal"
            element={
              <ProtectedRoute user={user}>
                <TerminalPanel user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          {/* Workshop Panel Route - Atölye kullanıcıları için */}
          <Route
            path="/workshop"
            element={
              <ProtectedRoute user={user}>
                <Workshop user={user} onLogout={handleLogout} selectedStore={selectedStore} />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes - Giriş yapmış kullanıcılar için */}
          <Route
            path="/"
            element={
              <ProtectedRoute user={user}>
                {isNativeApp ? (
                  <Navigate to="/terminal" replace />
                ) : user?.role === 'terminal' ? (
                  <Navigate to="/terminal" replace />
                ) : user?.role === 'atolye' ? (
                  <Navigate to="/workshop" replace />
                ) : (
                  <Dashboard
                    user={user}
                    onLogout={handleLogout}
                    stores={stores}
                    selectedStore={selectedStore}
                    onStoreChange={handleStoreChange}
                  />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/retail-sales"
            element={
              <ProtectedRoute user={user}>
                {(user?.role !== 'depo' || user?.permissions?.page_retail_sales) ? (
                  <RetailSales user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/repairs"
            element={
              <ProtectedRoute user={user}>
                {(user?.role !== 'depo' || user?.permissions?.page_repairs) ? (
                  <Repairs user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute user={user}>
                {(user?.role !== 'depo' || user?.permissions?.page_customers) ? (
                  <Customers user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute user={user}>
                {(user?.role !== 'depo' || user?.permissions?.page_products) ? (
                  <Products user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/stock-report"
            element={
              <ProtectedRoute user={user}>
                <StockReport user={user} selectedStore={selectedStore} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory-count"
            element={
              <ProtectedRoute user={user}>
                <InventoryCount user={user} selectedStore={selectedStore} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute user={user}>
                {(user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.page_reports)) ? (
                  <Reports user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute user={user}>
                {(user?.role !== 'depo' || user?.permissions?.page_settings) ? (
                  <Settings user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/label-design"
            element={
              <ProtectedRoute user={user}>
                <LabelDesign user={user} selectedStore={selectedStore} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scrap-purchase"
            element={
              <ProtectedRoute user={user}>
                {(user?.role !== 'depo' || user?.permissions?.page_scrap_purchase) ? (
                  <ScrapPurchase user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute user={user}>
                {(user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.page_campaigns)) ? (
                  <Campaigns user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/personnel"
            element={
              <ProtectedRoute user={user}>
                {(user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.page_personnel)) ? (
                  <PersonnelManagement user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/personnel/degisimfisi"
            element={
              <ProtectedRoute user={user}>
                {(user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.page_personnel)) ? (
                  <PersonnelManagement user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-calendar"
            element={
              <ProtectedRoute user={user}>
                {(user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.page_payment_calendar)) ? (
                  <PaymentCalendar user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/wholesaler-payments"
            element={
              <ProtectedRoute user={user}>
                {(user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.page_wholesaler_payments)) ? (
                  <WholesalerPayments user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/wholesalers"
            element={
              <ProtectedRoute user={user}>
                {(user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.page_wholesaler_payments)) ? (
                  <Wholesalers user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/wholesalers/:id"
            element={
              <ProtectedRoute user={user}>
                {(user?.role === 'admin' || (user?.role === 'depo' && user?.permissions?.page_wholesaler_payments)) ? (
                  <WholesalerDetail user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/shopify"
            element={<Navigate to="/marketplace/ayarlar" replace />}
          />
          <Route
            path="/trendyol"
            element={<Navigate to="/marketplace/ayarlar" replace />}
          />
          {/* Pazaryeri: yönetici ERP oturumuyla doğrudan Akik Pazar'a geçer (bkz. PazaryeriGateway);
              pazaryeri izni olan diğer kullanıcılar ERP'nin kendi ayarlarına gider */}
          <Route
            path="/marketplace"
            element={
              <ProtectedRoute user={user}>
                {user?.role === 'admin' ? (
                  <PazaryeriGateway />
                ) : user?.permissions?.canAccessShopify === true ? (
                  <Navigate to="/marketplace/ayarlar" replace />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/ayarlar"
            element={
              <ProtectedRoute user={user}>
                {(user?.role === 'admin' || user?.permissions?.canAccessShopify === true) ? (
                  <ShopifySettings user={user} selectedStore={selectedStore} />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          {/* Legal Pages - Public routes for App Store compliance */}
          <Route path="/legal" element={<Legal />} />
          <Route path="/legal/:page" element={<Legal />} />
          {/* Catch all - redirect to login or home */}
          <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
